import { LearnerEvaluation, Profile } from 'api';
import { LearnerForPurchase } from 'components/PurchaseProfiles/types';
import { Evaluator, ProfileType } from 'types/types';
import { getSubjectObjectTextKey, Pronoun } from 'domain/pronouns';
import { DIALECTS, DialectMap } from 'variables';

export const getDiscoveryProfile = (latestProfiles: Profile[]) => {
  return latestProfiles.find(
    (profile: any) => profile.profileType === 'DISCOVERY_PROFILE',
  );
};
export const getIDTLProfile = (latestProfiles: Profile[]) => {
  return latestProfiles.find(
    (profile: any) => profile?.profileType === 'IDTL_PROFILE',
  );
};

const isMissingDiscoveryProfile = (latestEvaluation: LearnerEvaluation) =>
  !getDiscoveryProfile(latestEvaluation.latestProfiles);

const alreadyHasIDTLProfile = (latestEvaluation: LearnerEvaluation) =>
  !!getIDTLProfile(latestEvaluation.latestProfiles);

const isClassicExploreEvaluator = (latestEvaluation: LearnerEvaluation) =>
  latestEvaluation.evaluatorType === 'CLASSIC_EXPLORE';

export const isUnableToPurchaseIDTL = (latestEvaluation: LearnerEvaluation) => {
  return (
    isMissingDiscoveryProfile(latestEvaluation) ||
    alreadyHasIDTLProfile(latestEvaluation) ||
    isClassicExploreEvaluator(latestEvaluation)
  );
};

export const hasPurchaseIDTLError = (purchaseItems: LearnerForPurchase[]) => {
  return purchaseItems.some((item: LearnerForPurchase) =>
    isUnableToPurchaseIDTL(item.latestEvaluation),
  );
};

export const hasPurchaseDiscoveryError = (
  purchaseItems: LearnerForPurchase[],
) => {
  return purchaseItems.some(
    (item: LearnerForPurchase) => item.assignedChapters.length < 1,
  );
};

export const hasArchivedProfile = (
  latestEvaluation: LearnerEvaluation,
  profileType: ProfileType,
): boolean =>
  !!latestEvaluation.archivedProfiles?.some(
    (profile) => profile.profileType === profileType,
  );

const ARCHIVED_PROFILE_TYPES_BY_PRODUCT: Partial<Record<string, ProfileType>> =
  {
    DISCOVERY_IMPACT_PROFILE: 'IMPACT_PROFILE',
    DISCOVERY_SELF_AWARE_PROFILE: 'DISCOVERY_PROFILE',
  };

export const getArchivedProfileType = (
  latestEvaluation: LearnerEvaluation,
  productName?: string,
): ProfileType | undefined => {
  const profileType =
    productName && ARCHIVED_PROFILE_TYPES_BY_PRODUCT[productName];
  return profileType && hasArchivedProfile(latestEvaluation, profileType)
    ? profileType
    : undefined;
};

const hasArchivedProfileForProduct = (
  purchaseItems: LearnerForPurchase[],
  productName: string,
): boolean =>
  purchaseItems.some((item: LearnerForPurchase) =>
    getArchivedProfileType(item.latestEvaluation, productName),
  );

export const isImpactProfileWithRetiringEvaluator = (
  evaluatorType: Evaluator,
) => {
  const blockedEvaluatorTypes: Evaluator[] = [
    'CLASSIC_EXPLORE',
    'DISCOVERY',
    'DFC',
  ];
  return blockedEvaluatorTypes.includes(evaluatorType);
};

export type PurchaseErrorReason =
  | 'IDTL_NEEDS_DISCOVERY'
  | 'IDTL_ALREADY_EXISTS'
  | 'PIP_NEEDS_DISCOVERY'
  | 'PIP_RETIRING_EVALUATOR';

export const getIDTLPurchaseErrorReason = (
  latestEvaluation: LearnerEvaluation,
): PurchaseErrorReason | undefined => {
  if (alreadyHasIDTLProfile(latestEvaluation)) return 'IDTL_ALREADY_EXISTS';
  if (isMissingDiscoveryProfile(latestEvaluation)) {
    return 'IDTL_NEEDS_DISCOVERY';
  }
  return undefined;
};

export const getPIPPurchaseErrorReason = (
  latestEvaluation: LearnerEvaluation,
): PurchaseErrorReason | undefined => {
  if (isImpactProfileWithRetiringEvaluator(latestEvaluation.evaluatorType)) {
    return 'PIP_RETIRING_EVALUATOR';
  }
  if (isMissingDiscoveryProfile(latestEvaluation)) {
    return 'PIP_NEEDS_DISCOVERY';
  }
  return undefined;
};

export const hasPurchasePIPError = (purchaseItems: LearnerForPurchase[]) => {
  return (
    purchaseItems.some((item: LearnerForPurchase) =>
      isMissingDiscoveryProfile(item.latestEvaluation),
    ) ||
    purchaseItems.some((item: LearnerForPurchase) =>
      isImpactProfileWithRetiringEvaluator(item.latestEvaluation.evaluatorType),
    ) ||
    hasArchivedProfileForProduct(purchaseItems, 'DISCOVERY_IMPACT_PROFILE')
  );
};

export const hasClassicExploreError = (
  purchaseItems: LearnerForPurchase[],
): boolean =>
  purchaseItems.some(
    (item) => item.latestEvaluation.evaluatorType === 'CLASSIC_EXPLORE',
  );

export const invalidProfilesPreferredLanguage: ProfileType[] = [
  'DISCOVERY_PERSONAL_PROFILE',
  'DISCOVERY_PROFILE',
];

export const isPreferredLanguageNotSupported = (
  dialect: DialectMap | undefined,
  productName?: string,
): boolean | undefined =>
  dialect &&
  productName === 'DISCOVERY_SELF_AWARE_PROFILE' &&
  invalidProfilesPreferredLanguage.some((item) =>
    dialect?.disabledForProfiles.includes(item),
  );

export const getPreferredDialectAndPronoun = (
  t: (key: string) => string,
  dialect: DialectMap | undefined,
  pronoun?: Pronoun,
): string => {
  const dialectKey = dialect && DIALECTS[dialect.value]?.textKey;
  const preferredDialectText = dialectKey
    ? t(dialectKey)
    : t('ui.event-management.learner.profiles.unsupported-language');
  return pronoun
    ? `${preferredDialectText} - ${t(getSubjectObjectTextKey(pronoun))}`
    : preferredDialectText;
};

export const shouldBlockPurchase = ({
  productName,
  purchaseItems,
}: {
  productName: string;
  purchaseItems: LearnerForPurchase[];
}): boolean => {
  const isIDTLproduct =
    productName === 'DISCOVERY_TRANSFORMATIONAL_LEADERSHIP_PROFILE';
  const isPIPproduct = productName === 'DISCOVERY_IMPACT_PROFILE';

  return (
    (isIDTLproduct && hasPurchaseIDTLError(purchaseItems)) ||
    (isPIPproduct && hasPurchasePIPError(purchaseItems)) ||
    hasPurchaseDiscoveryError(purchaseItems) ||
    hasClassicExploreError(purchaseItems) ||
    hasArchivedProfileForProduct(purchaseItems, productName)
  );
};
